import React from 'react';
import PlanPreviewCard from './PlanPreviewCard';
import Slider from './Slider';
import Modal, { ModalCloseButton } from '../../../shared/modals/Modal';
import { tiers } from '../../../shared/config/tiers';
import { useMessage } from '../../../contexts/MessageContext';
import { getPrice } from '../../../shared/lib/pricing';
import { buildSharedSubscriptionBenefits, formatBillingDate } from './subscriptionUtils';
import { ReactComponent as Spinner } from '../../../assets/icons/spinner2.svg';
import { ReactComponent as CheckIcon } from '../../../assets/icons/check.svg';

const Benefit = ({ text }) => (
  <div className="modal-check-list__item">
    <CheckIcon className="modal-check-list__icon" />
    <span>{text}</span>
  </div>
);

const SubscriptionsModal = ({ setSubscriptionsModalVisible, subscription, incrementSubscriptionVersion }) => {
  const showMessage = useMessage();
  const [processing, setProcessing] = React.useState(false);
  const [tier, setTier] = React.useState(0);
  const sharedBenefits = buildSharedSubscriptionBenefits();
  const closeModal = () => setSubscriptionsModalVisible(false);

  const handleUpdate = () => {
    if (processing) return;
    const cap = Math.round(tiers[tier].maxTraffic * tiers[tier].price);
    if (subscription?.status === 'active' && cap === subscription.cap) return;

    setProcessing(true);
    const token = localStorage.getItem('token');

    fetch(process.env.REACT_APP_SERVER_URL + '/api/change-subscription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
      },
      body: JSON.stringify({ cap }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data.success) {
          setProcessing(false);
          showMessage('Could not set subscription.');
          return;
        }

        incrementSubscriptionVersion();
        setProcessing(false);
        closeModal();
      })
      .catch(() => {
        setProcessing(false);
        showMessage('Could not set subscription.');
      });
  };

  const getCap = (nextTier) => {
    const { maxTraffic, price } = tiers[nextTier];
    return Math.round(maxTraffic * price);
  };

  const findTier = (cap) => {
    for (let i = 0; i < tiers.length; i += 1) {
      if (getCap(i) === cap) {
        return i;
      }
    }
    return 0;
  };

  const currentCap = subscription?.status === 'active' ? subscription.cap : null;
  const hasActiveSubscription = subscription?.status === 'active';
  const selectedCap = getCap(tier);
  const isCurrentSelection = hasActiveSubscription && selectedCap === currentCap;
  const currentTierIndex = currentCap ? findTier(currentCap) : 0;
  const amountDue = React.useMemo(() => {
    if (!subscription || subscription.status !== 'active') return null;
    return `$${getPrice(subscription.monthlyUsage ?? 0).toLocaleString()}`;
  }, [subscription]);
  const currentDetails = subscription?.status === 'active'
    ? [
      { label: 'Amount due so far', value: amountDue },
      { label: 'Next bill date', value: formatBillingDate(subscription.expiry) },
    ]
    : [];

  return (
    <Modal isOpen={true} onClose={closeModal} className="modal--large modal--full-height modal--subscription">
      <div className="modal">
        <div className="modal__header">
          <h3 className="margin-none">Update subscription</h3>
          <ModalCloseButton
            onClick={closeModal}
            label="Close subscription modal"
          />
        </div>

        <div className="modal__body">
          <div className="modal-section">
            <h4 className="modal-section__title">Choose monthly cap</h4>
            <div className="modal-control">
              <Slider
                currentCap={(hasActiveSubscription && subscription.cap) || 450}
                setTier={setTier}
              />
            </div>
          </div>

          <div className="modal-section">
            <h4 className="modal-section__title">
              {hasActiveSubscription ? 'Compare plans' : 'Available plans'}
            </h4>
            <div className={`modal-grid${hasActiveSubscription ? '' : ' modal-grid--single'}`}>
              {hasActiveSubscription && (
                <PlanPreviewCard
                  label="Current"
                  cap={subscription.cap}
                  numClicks={tiers[currentTierIndex].maxTraffic}
                  variant="current"
                  details={currentDetails}
                />
              )}
              <PlanPreviewCard
                label={hasActiveSubscription ? (isCurrentSelection ? 'Selected' : 'New') : 'Plan'}
                cap={selectedCap}
                numClicks={tiers[tier].maxTraffic}
                variant="new"
              />
            </div>
          </div>

          <div className="modal-section">
            <h4 className="modal-section__title">Included in every plan</h4>
            <div className="modal-check-list">
              {sharedBenefits.map((benefit) => (
                <Benefit key={benefit} text={benefit} />
              ))}
            </div>
          </div>
        </div>

        <div className="modal__footer">
          <button
            className="btn secondary"
            onClick={closeModal}
          >
            Cancel
          </button>
          <button
            className="btn primary"
            onClick={handleUpdate}
            disabled={processing || isCurrentSelection}
          >
            {processing && (
              <Spinner className="modal-submit-spinner" />
            )}
            <span>
              {processing ? 'Updating...' : isCurrentSelection ? 'Current plan selected' : 'Update'}
            </span>
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SubscriptionsModal;
